import { rage } from '~/rage';
import { notifyOnError } from '~/rage/decorators';
import { Subject } from '~/rage/utils';
import { ClientError, ClientErrorCode, IVector3 } from '~/shared';
import { IRpcService } from './RpcService';
import { loadCompleteScreenshot } from './ScreenshotLoader';
import { VarsService } from './VarsService';
import { HousesScreenshot } from './HousesScreenshot';

export type HouseScreenshotView = { url: string; canCopy: boolean } | null;

export class HousesEditorService {
	private readonly screenshotView = new Subject<HouseScreenshotView>(null);
	private image: HousesScreenshot | null = null;
	private screenshotGeneration = 0;
	private screenshotLoad?: AbortController;

	constructor(
		private readonly rpc: IRpcService,
		private readonly vars: VarsService,
	) {}
	public getPreviewing() {
		return this.vars.get('housesEditor:previewing', false);
	}
	public getPhotoActive() {
		return this.vars.get('housesEditor:photoActive', false);
	}
	public getBusy() {
		return this.vars.get('housesEditor:busy', false);
	}
	public getScreenshot() {
		return this.screenshotView;
	}

	@notifyOnError
	public getPosition(): Promise<IVector3> {
		return this.rpc.invokeClient('housesEditor:position');
	}
	@notifyOnError
	public preview(interiorId: string): Promise<void> {
		return this.rpc.invokeClient('housesEditor:preview', { interiorId });
	}
	@notifyOnError
	public returnToStart(): Promise<void> {
		return this.rpc.invokeClient('housesEditor:return');
	}
	@notifyOnError
	public hide(): Promise<void> {
		return this.rpc.invokeClient('housesEditor:hide');
	}
	@notifyOnError
	public close(): Promise<void> {
		return this.rpc.invokeClient('housesEditor:close');
	}
	@notifyOnError
	public preparePhoto(): Promise<void> {
		return this.rpc.invokeClient('housesEditor:photo');
	}
	@notifyOnError
	public stopPhoto(): Promise<void> {
		return this.rpc.invokeClient('housesEditor:stopPhoto');
	}

	@notifyOnError
	public async screenshot(): Promise<void> {
		this.clearScreenshot();
		const generation = this.screenshotGeneration;
		const filename = await this.rpc.invokeClient<string>(
			'housesEditor:screenshot',
		);
		if (generation !== this.screenshotGeneration) return;
		let blob: Blob;
		try {
			blob = await this.loadScreenshot(filename);
		} catch (error) {
			if (generation !== this.screenshotGeneration) return;
			throw error;
		}
		if (generation !== this.screenshotGeneration) return;
		const image = (this.image = new HousesScreenshot(blob));
		this.screenshotView.next({ url: image.url, canCopy: image.canCopy });
		const ready = image.ready.then(() => {
			if (this.image === image)
				this.screenshotView.next({ url: image.url, canCopy: true });
		});
		if (image.canCopy) void ready.catch(() => {});
		else
			await ready.catch((error) => {
				if (generation === this.screenshotGeneration) throw error;
			});
	}

	@notifyOnError
	public copyScreenshot(): Promise<void> {
		if (!this.image)
			throw new ClientError(ClientErrorCode.HousesEditorError, {
				message: 'Сначала сделайте снимок.',
			});
		return this.image.copy();
	}

	public clearScreenshot(): void {
		this.screenshotGeneration++;
		this.screenshotLoad?.abort();
		this.image?.dispose();
		this.image = null;
		this.screenshotView.next(null);
	}

	@notifyOnError
	public async copyLocationRow(row: string): Promise<void> {
		if (navigator.clipboard?.writeText) {
			try {
				await navigator.clipboard.writeText(row);
				return;
			} catch {
				/* CEF uses the shared copier below. */
			}
		}
		rage.input.copyToClipboard(row);
	}

	private async loadScreenshot(filename: string): Promise<Blob> {
		const controller = (this.screenshotLoad = new AbortController());
		const timeout = setTimeout(() => controller.abort(), 8000);
		try {
			return await loadCompleteScreenshot(filename, controller.signal);
		} catch (error) {
			throw new ClientError(ClientErrorCode.HousesEditorError, {
				message: 'Не удалось загрузить снимок. Попробуйте ещё раз.',
				stage: 'load',
				filename,
				reason: String(error),
			});
		} finally {
			clearTimeout(timeout);
			if (this.screenshotLoad === controller) this.screenshotLoad = undefined;
		}
	}
}
export type IHousesEditorService = Pick<
	HousesEditorService,
	keyof HousesEditorService
>;
